import { Entity } from '@brochington/ecstatic';
import { Components } from 'editor/components';
import { EditorModes } from 'editor/components/EditorMode';
import MoveBoxCPInteraction from 'editor/components/interactions/MoveBoxCPInteraction';
import MoveLineCPInteraction from 'editor/components/interactions/MoveLineCPInteraction';
import Markup from 'editor/components/Markup';
import Overlay from 'editor/components/Overlay';
import SheetEvents from 'editor/components/SheetEvents';
import {
  MouseDownArgs,
  MouseMoveArgs,
  MouseUpArgs,
} from 'editor/systems/addSheetEvents/sheetEventHandlers';
import {
  BoxControlPoint,
  boxControlPointHit,
  controlPointHitDelta,
  lineControlPointHit,
} from 'editor/utils/controlPoints';

interface HitEntity {
  entity: Entity<Components>;
  component: MoveBoxCPInteraction | MoveLineCPInteraction;
}

const moveControlPointEvents = {
  // TODO: This along with the pointEvents mouseDown, could use some tweaking.
  //       Maybe passing along the Markup Component instead of searching for it twice.
  mouseDown(args: MouseDownArgs): void {
    const { world, hit, markupKit, mode } = args;

    // Everything else in the function only has to do with non-viewonly mode.
    if (mode === EditorModes.ViewOnly) {
      return;
    }

    const cpHit = world
      .locateAll([Markup, Overlay])
      .map((entity) => entity.get(Markup))
      .filter((mc) => mc.selected)
      .some((mc) => {
        return (
          boxControlPointHit(mc, hit) !== BoxControlPoint.None ||
          lineControlPointHit(mc, hit) !== null
        );
      });

    if (!cpHit) {
      return;
    }

    world
      .locateAll([Markup, Overlay])
      .filter((entity) => entity.get(Markup).selected)
      .forEach((entity) => {
        const mc = entity.get(Markup);

        if (!mc.mkMarkup) {
          return;
        }

        // No control points on punches right now.
        if (mc.isPunch) {
          return;
        }

        // This needs to handle multiple types of markup control points.
        switch (mc.mkMarkup.getType()) {
          case 'Square':
            {
              const cph = boxControlPointHit(mc, hit);

              if (cph !== BoxControlPoint.None) {
                const mkm = mc.mkMarkup;

                if (!mkm) {
                  return;
                }

                const delta = controlPointHitDelta(mc, cph, hit.x, hit.y);

                world
                  .createEntity()
                  // .add(editorState)
                  .add(markupKit)
                  .add(mc)
                  .add(
                    new MoveBoxCPInteraction({
                      originX: hit.x,
                      originY: hit.y,
                      currentX: hit.x,
                      currentY: hit.y,
                      deltaX: delta.x,
                      deltaY: delta.y,
                      controlPoint: cph,
                    })
                  );
              }
            }
            break;
          case 'FreeText':
            {
              const cph = boxControlPointHit(mc, hit);

              if (cph !== BoxControlPoint.None) {
                const mkm = mc.mkMarkup;

                if (!mkm) {
                  return;
                }

                const delta = controlPointHitDelta(mc, cph, hit.x, hit.y);

                world
                  .createEntity()
                  // .add(editorState)
                  .add(markupKit)
                  .add(mc)
                  .add(
                    new MoveBoxCPInteraction({
                      originX: hit.x,
                      originY: hit.y,
                      currentX: hit.x,
                      currentY: hit.y,
                      deltaX: delta.x,
                      deltaY: delta.y,
                      controlPoint: cph,
                    })
                  );
              }
            }
            break;
          case 'Line':
            {
              const cph = lineControlPointHit(mc, hit);

              if (cph) {
                world
                  .createEntity()
                  .add(markupKit)
                  .add(mc)
                  .add(
                    new MoveLineCPInteraction({
                      pointIndex: cph.pointIndex,
                      originX: hit.x,
                      originY: hit.y,
                      currentX: hit.x,
                      currentY: hit.y,
                    })
                  );
              }
            }
            break;
          default:
        }
      });
  },

  mouseMove(args: MouseMoveArgs): void {
    const { world, hit } = args;

    world.grabAll(MoveBoxCPInteraction).forEach(({ component }) => {
      component.currentX = hit.x;
      component.currentY = hit.y;
    });

    world.grabAll(MoveLineCPInteraction).forEach(({ component }) => {
      component.currentX = hit.x;
      component.currentY = hit.y;
    });
  },

  mouseUp(args: MouseUpArgs): void {
    const { world, hit } = args;

    function updateMarkup(hitEntity: HitEntity) {
      const { entity } = hitEntity;
      const mc = entity.get(Markup);
      const { mkMarkup } = mc;

      if (mkMarkup) {
        hitEntity.component.currentX = hit.x;
        hitEntity.component.currentY = hit.y;
        world.grabAll(SheetEvents).forEach(({ component: eventsComp }) => {
          eventsComp.emit('move:controlpoint:mouseup', { markupComp: mc });
        });
      }

      entity.destroyImmediately();
    }

    world.grabAll(MoveBoxCPInteraction).forEach(updateMarkup);
    world.grabAll(MoveLineCPInteraction).forEach(updateMarkup);
  },
};

export default moveControlPointEvents;
